import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { RoomAccessibility } from "@uno/protocol";
import ButtonHapticWrapper from "component/Button/ButtonHapticWrapper";
import useRoomNavigation from "hook/useRoomNavigation";
import Container from "component/Layout/Container";
import Popover from "component/Popover";
import SettingsDropdown from "component/Ui/SettingsDropdown";
import getPublicAssetUrl from "utils/PublicAsset";
import "./index.scss";

const Home = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { createRoom, joinRoom } = useRoomNavigation();
    const [showJoinInput, setShowJoinInput] = React.useState(false);
    const [showMoreMenu, setShowMoreMenu] = React.useState(false);
    const [joinCode, setJoinCode] = React.useState("");

    const handleJoinByCode = (e: React.FormEvent) => {
        e.preventDefault();
        const code = joinCode.trim();

        if(!code) {
            return;
        }

        joinRoom(code);
    };

    return (
        <Container id="home" headerLeft={<SettingsDropdown />}>
            <main className="flex-1 flex flex-col lg:flex-row viewport-compact:flex-row items-center justify-center px-4 lg:px-8 viewport-compact:px-[clamp(2rem,5vw,4rem)] py-6 lg:py-0 viewport-compact:py-[clamp(0.75rem,3vh,2rem)] gap-8 lg:gap-20 viewport-compact:gap-[clamp(1.5rem,5vw,5rem)] viewport-compact:min-h-0">
                <section className="flex-1 flex lg:flex-1 flex-col items-center lg:items-start viewport-compact:items-center justify-center viewport-compact:min-w-0">
                    <div className="relative">
                        <div className="absolute inset-0 -z-10 pointer-events-none scale-[1.6]" style={{ background: "radial-gradient(ellipse at center, rgba(255,255,255,0.32) 0%, rgba(255,255,255,0.14) 35%, rgba(255,255,255,0) 75%)", filter: "blur(20px)" }} />
                        <img src={getPublicAssetUrl("assets/logo.webp")} alt="Logo" className="logo-heartbeat relative w-[75vw] lg:w-[440px] viewport-compact:w-[clamp(17rem,38vw,23rem)] h-auto drop-shadow-2xl" style={{ filter: "drop-shadow(0 0 60px rgba(34,211,238,0.35))" }} />
                    </div>
                    <p className="-mt-4 ml-[3%] viewport-compact:ml-0 text-white/60 text-center text-sm lg:text-base viewport-compact:text-[clamp(0.875rem,2vh,1rem)] font-changa-one tracking-wide max-w-xs lg:max-w-sm self-center lg:self-auto lg:w-[440px] viewport-compact:w-[min(44vw,440px)]">
                        {t("home.tagline")}
                    </p>
                </section>

                <section className="z-10 w-full lg:max-w-sm viewport-compact:max-w-sm viewport-compact:flex-[0_1_24rem] flex flex-col items-center gap-3 mt-auto mb-4 lg:mt-0 lg:mb-0 viewport-compact:mt-0 viewport-compact:mb-0">
                    <div className="w-full relative">
                        <ButtonHapticWrapper>
                            <button
                                onClick={() => createRoom()}
                                className="btn-yellow w-full items-center justify-between"
                            >
                                <span className="line-clamp-1 text-left font-changa-one">{ t("home.play_a_game") }</span>
                                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                                    <path fillRule="evenodd" clipRule="evenodd" d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22ZM10.6935 15.8458L15.4137 13.059C16.1954 12.5974 16.1954 11.4026 15.4137 10.941L10.6935 8.15419C9.93371 7.70561 9 8.28947 9 9.21316V14.7868C9 15.7105 9.93371 16.2944 10.6935 15.8458Z" fill="currentColor"/>
                                </svg>
                            </button>
                        </ButtonHapticWrapper>
                    </div>

                    <div className="w-full relative">
                        <ButtonHapticWrapper>
                            <button
                                onClick={() => createRoom(RoomAccessibility.Private)}
                                className="btn-white w-full items-center justify-between"
                            >
                                <span className="line-clamp-1 text-left font-changa-one">{ t("home.create_a_private_game") }</span>
                                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm5 11h-4v4h-2v-4H7v-2h4V7h2v4h4v2z"/>
                                </svg>
                            </button>
                        </ButtonHapticWrapper>
                    </div>

                    {showJoinInput ? (
                        <form
                            className="w-full flex items-center gap-2"
                            onSubmit={handleJoinByCode}
                        >
                            <input
                                type="text"
                                maxLength={4}
                                value={joinCode}
                                onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
                                placeholder="XXXX"
                                autoFocus
                                className="flex-1 h-12 rounded-full bg-white/10 border border-white/20 text-center text-white font-changa-one text-lg tracking-[0.3em] uppercase placeholder:text-white/30 outline-none focus:border-white/40"
                            />
                            {joinCode.trim() ? (
                                <ButtonHapticWrapper>
                                    <button type="submit" className="btn-cyan h-12 px-5 justify-center">
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                                        </svg>
                                    </button>
                                </ButtonHapticWrapper>
                            ) : (
                                <ButtonHapticWrapper>
                                    <button
                                        type="button"
                                        onClick={() => { setShowJoinInput(false); setJoinCode(""); }}
                                        aria-label={t("home.cancel")}
                                        className="btn-white h-12 px-5 justify-center"
                                    >
                                        <svg className="relative z-10 w-3 h-3 fill-[#1f2937]" viewBox="0 0 25 25">
                                            <path d="M487.148,1053.48 L492.813,1047.82 C494.376,1046.26 494.376,1043.72 492.813,1042.16 C491.248,1040.59 488.712,1040.59 487.148,1042.16 L481.484,1047.82 L475.82,1042.16 C474.257,1040.59 471.721,1040.59 470.156,1042.16 C468.593,1043.72 468.593,1046.26 470.156,1047.82 L475.82,1053.48 L470.156,1059.15 C468.593,1060.71 468.593,1063.25 470.156,1064.81 C471.721,1066.38 474.257,1066.38 475.82,1064.81 L481.484,1059.15 L487.148,1064.81 C488.712,1066.38 491.248,1066.38 492.813,1064.81 C494.376,1063.25 494.376,1060.71 492.813,1059.15 L487.148,1053.48" transform="translate(-469, -1041)" />
                                        </svg>
                                    </button>
                                </ButtonHapticWrapper>
                            )}
                        </form>
                    ) : (
                        <div className="w-full flex items-center gap-3">
                            <ButtonHapticWrapper>
                                <button onClick={() => setShowJoinInput(true)} className="btn-cyan flex-1 justify-center">
                                    <span className="line-clamp-1 text-left font-changa-one">{ t("home.join_a_game") }</span>
                                </button>
                            </ButtonHapticWrapper>
                            <span className="shrink-0 text-white/40 font-changa-one text-xs uppercase tracking-[0.2em]">{t("home.or")}</span>
                            <Popover
                                visible={showMoreMenu}
                                onClose={() => setShowMoreMenu(false)}
                                placement="top"
                                className="shrink-0"
                                content={
                                    <div className="rounded-2xl bg-gray-900/95 border border-white/10 shadow-2xl overflow-hidden w-56 max-w-[calc(100vw-2rem)]">
                                        <button
                                            type="button"
                                            onClick={() => { setShowMoreMenu(false); navigate("/leaderboard"); }}
                                            className="w-full flex items-center gap-3 px-4 py-3 text-left text-white hover:bg-white/10 cursor-pointer"
                                        >
                                            <svg className="w-5 h-5 shrink-0" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M6.67 14H4C2.9 14 2 14.9 2 16V21C2 21.55 2.45 22 3 22H6.67C7.22 22 7.67 21.55 7.67 21V15C7.67 14.45 7.22 14 6.67 14Z" />
                                                <path d="M13.3302 10H10.6602C9.56016 10 8.66016 10.9 8.66016 12V21C8.66016 21.55 9.11016 22 9.66016 22H14.3302C14.8802 22 15.3302 21.55 15.3302 21V12C15.3302 10.9 14.4402 10 13.3302 10Z" />
                                                <path d="M20.0001 17H17.3301C16.7801 17 16.3301 17.45 16.3301 18V21C16.3301 21.55 16.7801 22 17.3301 22H21.0001C21.5501 22 22.0001 21.55 22.0001 21V19C22.0001 17.9 21.1001 17 20.0001 17Z" />
                                                <path d="M15.0095 4.85047C15.3195 4.54047 15.4395 4.17047 15.3395 3.85047C15.2395 3.53047 14.9295 3.30047 14.4895 3.23047L13.5295 3.07047C13.4895 3.07047 13.3995 3.00047 13.3795 2.96047L12.8495 1.90047C12.4495 1.09047 11.5395 1.09047 11.1395 1.90047L10.6095 2.96047C10.5995 3.00047 10.5095 3.07047 10.4695 3.07047L9.50945 3.23047C9.06945 3.30047 8.76945 3.53047 8.65945 3.85047C8.55945 4.17047 8.67945 4.54047 8.98945 4.85047L9.72945 5.60047C9.76945 5.63047 9.79945 5.75047 9.78945 5.79047L9.57945 6.71047C9.41945 7.40047 9.67945 7.71047 9.84945 7.83047C10.0195 7.95047 10.3895 8.11047 10.9995 7.75047L11.8995 7.22047C11.9395 7.19047 12.0695 7.19047 12.1095 7.22047L12.9995 7.75047C13.2795 7.92047 13.5095 7.97047 13.6895 7.97047C13.8995 7.97047 14.0495 7.89047 14.1395 7.83047C14.3095 7.71047 14.5695 7.40047 14.4095 6.71047L14.1995 5.79047C14.1895 5.74047 14.2195 5.63047 14.2595 5.60047L15.0095 4.85047Z" />
                                            </svg>
                                            <span className="font-changa-one text-sm tracking-wide">{t("leaderboard.title")}</span>
                                        </button>
                                        <div className="h-px bg-white/10" />
                                        <button
                                            type="button"
                                            disabled
                                            className="w-full flex items-center gap-3 px-4 py-3 text-left text-white/40 cursor-not-allowed"
                                        >
                                            <svg className="w-5 h-5 shrink-0" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M9 2H2v2h5v4H2v2h7V7h5v10H9v-3H2v2h5v4H2v2h7v-3h7v-6h6v-2h-6V5H9V2z" />
                                            </svg>
                                            <span className="font-changa-one text-sm tracking-wide">{t("home.tournaments")}</span>
                                            <span className="ml-auto text-[9px] uppercase tracking-wider text-white/40 bg-white/5 border border-white/10 rounded-full px-1.5 py-0.5">{t("home.soon")}</span>
                                        </button>
                                    </div>
                                }
                            >
                                <ButtonHapticWrapper>
                                    <button
                                        type="button"
                                        onClick={() => setShowMoreMenu(!showMoreMenu)}
                                        aria-label={t("home.more")}
                                        className="h-12 px-5 rounded-full bg-white/10 hover:bg-white/15 border border-white/10 text-white/70 hover:text-white transition-colors flex items-center justify-center cursor-pointer"
                                    >
                                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                            <circle cx="5" cy="12" r="2" />
                                            <circle cx="12" cy="12" r="2" />
                                            <circle cx="19" cy="12" r="2" />
                                        </svg>
                                    </button>
                                </ButtonHapticWrapper>
                            </Popover>
                        </div>
                    )}
                </section>
            </main>
        </Container>
    );
};

export default Home;
